import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Scale } from 'lucide-react';
import { ADMIN_PALETTE } from './adminDesignSystem';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FIELDS = [
  { key: 'company_name', label: 'Legal company name (Greek)', placeholder: 'ALLURE EXPERIENCE I.K.E.', help: 'Full legal name in the national language. Shown on every signed agreement.' },
  { key: 'company_name_latin', label: 'Legal company name (Latin)', placeholder: 'ALLURE EXPERIENCE P.C.', help: 'Latin transliteration for EU / international counter-parties.' },
  { key: 'distinctive_title', label: 'Distinctive title', placeholder: 'ALLUREXP', help: 'The commercial trading name.' },
  { key: 'legal_form', label: 'Legal form', placeholder: 'IKE (Private Company)', help: 'Company type as registered with GEMI.' },
  { key: 'gemi_number', label: 'Γ.Ε.ΜΗ. number', placeholder: '194817609000', help: 'Greek General Commercial Registry number.' },
  { key: 'euid', label: 'EUID', placeholder: 'ELGEMI.194817609000', help: 'European Unique Identifier assigned by GEMI.' },
  { key: 'chamber', label: 'Chamber of Commerce', placeholder: 'Επαγγελματικό Επιμελητήριο Πειραιώς', help: 'Competent chamber the company is registered with.' },
  { key: 'vat_number', label: 'VAT / TIN (ΑΦΜ)', placeholder: 'EL803335274', help: 'Prefix with country code (EL for Greece) on invoices.' },
  { key: 'tax_office', label: 'Tax office (ΔΟΥ)', placeholder: 'ΚΕΦΟΔΕ ΑΤΤΙΚΗΣ', help: 'Competent tax office where the ΑΦΜ is registered.' },
  { key: 'address', label: 'Registered address (Greek)', placeholder: 'Εμμανουήλ Δημελά 74, 16674 Αθήνα, Ελλάδα', help: 'Full postal address of the registered seat.' },
  { key: 'address_latin', label: 'Registered address (Latin)', placeholder: 'Emmanouil Dimela 74, 16674 Athens, Greece', help: 'Latin transliteration for international agreements.' },
  { key: 'phone', label: 'Contact phone', placeholder: '+30 694 396 3291', help: 'Public phone number for the company.' },
  { key: 'website', label: 'Website', placeholder: 'https://allurexp.com', help: 'Primary website. Used in agreement links to Privacy Policy.' },
  { key: 'legal_representative', label: 'Legal representative / Administrator', placeholder: 'Horatiu Marian Onofrei', help: 'Person who legally binds the company (Διαχειριστής).' },
  { key: 'support_email', label: 'Support email', placeholder: 'contact@allurexp.com', help: 'General customer-facing inbox.' },
  { key: 'legal_email', label: 'Legal-notices email', placeholder: 'contact@allurexp.com', help: 'Where venues & hosts send formal legal notices. Can be the same as support.' },
  { key: 'dpo_email', label: 'DPO / GDPR email', placeholder: 'contact@allurexp.com', help: 'Where data-subject access requests are sent. Can be the same as support.' },
  { key: 'jurisdiction', label: 'Governing law', placeholder: 'Greece (EU)', help: 'Applied to the dispute-resolution clause.' },
  { key: 'sepa_fee_eur', label: 'SEPA payout fee (EUR)', placeholder: '0.80', help: 'Extra per-transaction fee retained when a Venue connects a non-Viva IBAN.' },
  { key: 'shareholders', label: 'Shareholders / UBO (admin-only)', placeholder: 'Name (ΑΦΜ …) — % (N shares)', help: 'Ownership breakdown for KYC / UBO records. Only visible to platform admins — never exposed on public pages beyond partner names on the Imprint.', multiline: true },
];

export function AdminLegalSettingsPanel({ headers }) {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/legal-settings`, { headers });
      setValues(res.data || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load legal settings');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleChange = (key, value) => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/legal-settings`, values, { headers });
      setValues(res.data || values);
      toast.success('Legal settings saved — new agreements will use these values.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-legal-settings-panel" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <Scale size={18} color={ADMIN_PALETTE.brandRed} />
        <h2 style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 22, fontWeight: 700, color: ADMIN_PALETTE.ink }}>
          Legal Entity Settings
        </h2>
      </div>
      <p style={{ margin: 0, fontSize: 12.5, color: ADMIN_PALETTE.inkMuted, maxWidth: 640 }}>
        These values populate every new venue and host agreement. <strong>Already-signed agreements remain frozen at the values shown at the time of signing</strong> — bumping the VAT here will only affect new sign-ups.
      </p>

      {loading ? (
        <p style={{ color: ADMIN_PALETTE.inkMuted, fontSize: 13 }}>Loading…</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {FIELDS.map((field) => (
            <div
              key={field.key}
              style={{
                padding: 14,
                borderRadius: 14,
                background: ADMIN_PALETTE.surface,
                border: `1px solid ${ADMIN_PALETTE.border}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <label
                style={{
                  fontFamily: 'inherit',
                  fontSize: 12,
                  fontWeight: 700,
                  color: ADMIN_PALETTE.inkSoft,
                  letterSpacing: '0.03em',
                  textTransform: 'uppercase',
                }}
                htmlFor={`legal-settings-${field.key}`}
              >
                {field.label}
              </label>
              {field.multiline ? (
                <textarea
                  id={`legal-settings-${field.key}`}
                  value={values[field.key] || ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  rows={4}
                  data-testid={`admin-legal-settings-${field.key}`}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: `1px solid ${ADMIN_PALETTE.border}`,
                    background: ADMIN_PALETTE.surfaceMuted,
                    fontSize: 14,
                    color: ADMIN_PALETTE.ink,
                    fontFamily: 'inherit',
                    outline: 'none',
                    resize: 'vertical',
                    minHeight: 88,
                    lineHeight: 1.45,
                  }}
                />
              ) : (
                <input
                  id={`legal-settings-${field.key}`}
                  value={values[field.key] || ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  data-testid={`admin-legal-settings-${field.key}`}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: `1px solid ${ADMIN_PALETTE.border}`,
                    background: ADMIN_PALETTE.surfaceMuted,
                    fontSize: 14,
                    color: ADMIN_PALETTE.ink,
                    fontFamily: 'inherit',
                    outline: 'none',
                  }}
                />
              )}
              <span style={{ fontSize: 11.5, color: ADMIN_PALETTE.inkMuted }}>{field.help}</span>
            </div>
          ))}

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            data-testid="admin-legal-settings-save"
            style={{
              alignSelf: 'flex-start',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              padding: '10px 16px',
              borderRadius: 12,
              border: 'none',
              background: ADMIN_PALETTE.ink,
              color: '#FFFFFF',
              fontWeight: 700,
              fontSize: 13,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}
          >
            <Save size={14} />
            {saving ? 'Saving…' : 'Save legal settings'}
          </button>
        </div>
      )}
    </div>
  );
}
